export const BRIDGE_VERSION = '2.2.0';

export type NativePlatform = 'ios' | 'android';

export type HapticStyle =
  | 'light'
  | 'medium'
  | 'heavy'
  | 'success'
  | 'warning'
  | 'error';

export interface NativeAuthUser {
  id: string;
  email: string;
  fullName: string;
  phone: string;
}

export interface NativeAuthData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: NativeAuthUser | null;
}

export interface NativeGlobal {
  platform: NativePlatform;
  appVersion: string;
  bridgeVersion: string;
  auth: NativeAuthData | null;
}

interface BaseMessage<TType extends string, TPayload = undefined> {
  id: string;
  type: TType;
  payload?: TPayload;
  timestamp: number;
  version: string;
}

// ---------------- Web → Native ----------------

export type WebReadyMessage = BaseMessage<'WEB_READY'>;

export interface AuthTokenExpiredPayload {
  statusCode: number;
  requestUrl: string;
  canRetry: boolean;
}
export type AuthTokenExpiredMessage = BaseMessage<
  'AUTH_TOKEN_EXPIRED',
  AuthTokenExpiredPayload
>;

export interface AuthTokenExpiredResponsePayload {
  success: boolean;
  newToken?: string;
  requireRelogin?: boolean;
}
export type AuthTokenExpiredResponseMessage = BaseMessage<
  'AUTH_TOKEN_EXPIRED_RESPONSE',
  AuthTokenExpiredResponsePayload
>;

export type AuthLogoutFromWebMessage = BaseMessage<'AUTH_LOGOUT_FROM_WEB'>;
export type AuthLogoutFromWebResponseMessage = BaseMessage<
  'AUTH_LOGOUT_FROM_WEB_RESPONSE',
  { success: boolean }
>;

export type PushRegisterRequestMessage = BaseMessage<'PUSH_REGISTER_REQUEST'>;
export interface PushRegisterResponsePayload {
  success: boolean;
  token: string | null;
  error?: 'PERMISSION_DENIED' | 'NOT_SUPPORTED' | 'UNKNOWN';
}
export type PushRegisterResponseMessage = BaseMessage<
  'PUSH_REGISTER_RESPONSE',
  PushRegisterResponsePayload
>;

export type HapticFeedbackMessage = BaseMessage<
  'HAPTIC_FEEDBACK',
  { style: HapticStyle }
>;

export type OpenExternalUrlMessage = BaseMessage<
  'OPEN_EXTERNAL_URL',
  { url: string }
>;

export interface DownloadFilePayload {
  filename: string;
  mimeType: string;
  /** Raw base64, no `data:` prefix. */
  base64: string;
  /** Pre-encoded blob size for native-side cross-check (hard cap 10MB). */
  sizeBytes: number;
}
export type DownloadFileMessage = BaseMessage<'DOWNLOAD_FILE', DownloadFilePayload>;

export interface DownloadFileResponsePayload {
  success: boolean;
  /**
   * Reserved for forward compat. Native using `expo-sharing` cannot tell save
   * from cancel on iOS — treat as undefined and branch only on `success`.
   */
  cancelled?: boolean;
  error?: 'WRITE_FAILED' | 'SIZE_LIMIT' | 'UNKNOWN';
  errorMessage?: string;
}
export type DownloadFileResponseMessage = BaseMessage<
  'DOWNLOAD_FILE_RESPONSE',
  DownloadFileResponsePayload
>;

export type WebToNativeMessage =
  | WebReadyMessage
  | AuthTokenExpiredMessage
  | AuthLogoutFromWebMessage
  | PushRegisterRequestMessage
  | HapticFeedbackMessage
  | OpenExternalUrlMessage
  | DownloadFileMessage;

// ---------------- Native → Web ----------------

export interface AuthTokenRefreshedPayload {
  accessToken: string;
  expiresAt: number;
}
export type AuthTokenRefreshedMessage = BaseMessage<
  'AUTH_TOKEN_REFRESHED',
  AuthTokenRefreshedPayload
>;

export interface PushOpenedPayload {
  deeplink?: string;
  orderId?: string;
  orderType?: 'BUSINESS' | 'INDIVIDUAL' | 'GUEST';
  notificationType?: string;
  [key: string]: unknown;
}
export type PushOpenedMessage = BaseMessage<'PUSH_OPENED', PushOpenedPayload>;

export interface DeeplinkOpenedPayload {
  url: string;
  path?: string;
}
export type DeeplinkOpenedMessage = BaseMessage<
  'DEEPLINK_OPENED',
  DeeplinkOpenedPayload
>;

export type AppResumedMessage = BaseMessage<
  'APP_RESUMED',
  { backgroundDurationMs: number }
>;

export interface NetworkStatusChangedPayload {
  connected: boolean;
  type: 'wifi' | 'cellular' | 'none' | 'unknown';
}
export type NetworkStatusChangedMessage = BaseMessage<
  'NETWORK_STATUS_CHANGED',
  NetworkStatusChangedPayload
>;

export type ResponseMessage =
  | AuthTokenExpiredResponseMessage
  | AuthLogoutFromWebResponseMessage
  | PushRegisterResponseMessage
  | DownloadFileResponseMessage;

export type NativeToWebMessage =
  | AuthTokenRefreshedMessage
  | PushOpenedMessage
  | DeeplinkOpenedMessage
  | AppResumedMessage
  | NetworkStatusChangedMessage
  | ResponseMessage;

export type BridgeMessage = WebToNativeMessage | NativeToWebMessage;

// Outbound messages, as authored by the caller (no envelope fields yet).
export type OutboundMessageInput<TMsg extends WebToNativeMessage> = Omit<
  TMsg,
  'id' | 'timestamp' | 'version'
>;

export type NativeMessageType = NativeToWebMessage['type'];

export type NativeMessageHandler<T extends NativeMessageType> = (
  payload: Extract<NativeToWebMessage, { type: T }>['payload'],
  message: Extract<NativeToWebMessage, { type: T }>,
) => void;

declare global {
  interface Window {
    __VMALL_NATIVE__?: NativeGlobal;
    ReactNativeWebView?: {
      postMessage: (data: string) => void;
    };
  }
}

export {};
